import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, MotionConfig, motion } from "framer-motion";
import {
  AlertTriangle,
  ArrowRight,
  Check,
  Clock,
  FileText,
  History,
  Info,
  MessageCircleQuestion,
  PenLine,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";

export type GenerationPanelState = "idle" | "running" | "done" | "failed";

export interface GenerationProgressDetails {
  /** Customer questions extracted by query understanding. */
  questions: string[] | null;
  /** Search queries the retrieval step actually ran. */
  searchQueries: string[] | null;
  /** Raw number of retrieval hits before reranking (null until retrieval ran). */
  searchHitCount: number | null;
  /** Identifiers (machine numbers etc.) recognized in the ticket. */
  extractedIdentifiers: string[] | null;
  /** Reranked references with title + relevance. */
  references:
    | { index: number; title: string | null; relevance: string | null; snippet: string | null }[]
    | null;
  /** Prior-case lines the memory step considered (null until that step ran). */
  facts: string[] | null;
}

export interface GenerationMailContext {
  senderName: string | null;
  subject: string | null;
  receivedAt: Date | null;
}

/** Progress of the email-intake phase that precedes the generation pipeline:
 *  "fetching" while the mail is still being pulled into Lytra, "annotating"
 *  once it has arrived and the request is being analyzed, "done" when the
 *  generation pipeline has taken over. */
export type GenerationSyncPhase = "fetching" | "annotating" | "done";

interface GenerationProgressPanelProps {
  state: GenerationPanelState;
  /** Stages received from the proposedAnswerProgress subscription so far. */
  seenStages: ReadonlySet<string>;
  /** Most recent stage event (null before the first event arrives). */
  /** Live work product from the latest progress snapshot. */
  details?: GenerationProgressDetails;
  errorMessage?: string | null;
  /** Prepend the synthetic intake rows ("E-Mail empfangen", request analysis)
   *  ahead of the pipeline tasks, so the email-sync phase and the generation
   *  pipeline read as one continuous journey. */
  includeSyncTask?: boolean;
  /** Which intake row is active. While not "done", the intake phase owns the
   *  spinner and all pipeline tasks render as pending. */
  syncPhase?: GenerationSyncPhase;
  /** Sender/subject/time of the open email, rendered as a context card above
   *  the header. Sub-elements whose datum is missing are omitted. */
  mailContext?: GenerationMailContext | null;
  onContinue: () => void;
  onRetry: () => void;
}

// Pipeline order of the broadcast stages. A late subscriber (ticket opened
// mid-generation) may have missed earlier events, so a task also counts as
// finished when any later-ranked stage has been observed.
const STAGE_RANK: Record<string, number> = {
  started: 0,
  memory_looked_up: 1,
  facts_found: 2,
  generating: 3,
  generated: 4,
  scored: 5,
  refining: 6,
  refined: 7,
  completed: 8,
};

// Elapsed-time thresholds for the overrun escalation while running.
const OVERRUN_SUBTITLE_SECONDS = 60;
const OVERRUN_ACTIONS_SECONDS = 120;
const OVERRUN_ACTIONS_REAPPEAR_SECONDS = 180;

type ActKey = "intake" | "analysis" | "answer";

interface TaskDef {
  key: string;
  doneRank: number;
  /** Section the task row is grouped under. */
  act: ActKey;
  // Conditional tasks stay hidden until their trigger stage shows up
  // (low-confidence refinement).
  appearsOnStages?: string[];
}

const TASKS: TaskDef[] = [
  { key: "memory", doneRank: STAGE_RANK.memory_looked_up, act: "analysis" },
  // One row for the whole fact-finding loop. It picks its own next query from
  // what the last one returned, so how many searches it runs is not something
  // the panel can draw ahead of time -- and a row per search would flicker.
  { key: "search", doneRank: STAGE_RANK.facts_found, act: "analysis" },
  { key: "generate", doneRank: STAGE_RANK.generated, act: "answer" },
  {
    key: "refine",
    doneRank: STAGE_RANK.refined,
    act: "answer",
    appearsOnStages: ["refining", "refined"],
  },
];

function isTaskDone(task: TaskDef, maxSeenRank: number): boolean {
  return maxSeenRank >= task.doneRank;
}

function senderInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0][0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? "") : "";
  return (first + last).toUpperCase() || "?";
}

export function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** Small uppercase section label with a hairline rule, grouping task rows.
 *  Acts as a full-width divider between rail segments (the rail does not run
 *  through it — each act owns its own connected segment). Animates in
 *  alongside the first row of its section. */
function ActLabel({ label }: { label: string }) {
  return (
    <motion.li
      aria-hidden="true"
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="flex items-center gap-2 px-2 pt-2.5 pb-1.5 first:pt-0"
    >
      <span className="text-[9px] font-semibold uppercase tracking-widest text-muted-foreground/70">
        {label}
      </span>
      <span className="h-px flex-1 bg-border" />
    </motion.li>
  );
}

type RailNodeState = "active" | "done" | "failed";

/** The rail circle for a row. Done = deep-blue disc with a spring-in check.
 *  Active = a rotating conic-gradient ring with a white inset (the single
 *  ambient looping signal); under reduced motion it falls back to a static
 *  gradient disc that still reads as in-progress. Failed = muted disc with a
 *  warning glyph (destructive red is reserved for the failure card). */
function RailNode({ state }: { state: RailNodeState }) {
  if (state === "done") {
    return (
      <motion.span
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 420, damping: 22 }}
        className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-primary text-primary-foreground"
      >
        <Check className="h-2.5 w-2.5" strokeWidth={3.5} />
      </motion.span>
    );
  }
  if (state === "failed") {
    return (
      <span className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-muted-foreground/70 text-background">
        <AlertTriangle className="h-2.5 w-2.5" strokeWidth={2.6} />
      </span>
    );
  }
  // Active: static subtle outer ring + spinning conic gradient with white inset.
  return (
    <span className="relative flex h-[18px] w-[18px] items-center justify-center">
      <span
        aria-hidden="true"
        className="absolute rounded-full"
        style={{ inset: "-4px", border: "1.5px solid rgba(52,90,239,0.18)" }}
      />
      <span
        aria-hidden="true"
        className="rail-node-spin relative h-[18px] w-[18px] rounded-full"
        style={{
          background:
            "conic-gradient(from 0deg, #345AEF, #80E4FB, #345AEF)",
        }}
      >
        <span
          className="absolute rounded-full bg-card"
          style={{ inset: "2.5px" }}
        />
      </span>
    </span>
  );
}

/** A 2px vertical connector running down from a row's node to the next row in
 *  the same act segment. Fills top-down with the brand gradient once the row
 *  above is done; otherwise it stays the neutral hairline. */
function RailConnector({ filled }: { filled: boolean }) {
  return (
    <div className="relative mt-1 w-0.5 min-h-[10px] flex-1 overflow-hidden rounded-full bg-border">
      <motion.span
        aria-hidden="true"
        className="absolute inset-0 origin-top rounded-full"
        style={{ background: "linear-gradient(180deg, #345AEF, #80E4FB)" }}
        initial={false}
        animate={{ scaleY: filled ? 1 : 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      />
    </div>
  );
}

/** Expanding container for per-task live detail. Sits inside the row's content
 *  column, so it indents to the body edge (no extra left padding — the row's
 *  rail/body grid already provides the indentation). */
function DetailReveal({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className="overflow-hidden"
    >
      <div className="pr-1 pt-0.5 pb-1.5 space-y-1">{children}</div>
    </motion.div>
  );
}

/** One compact proof-of-work line per task, rendered only from real data.
 *  When the underlying field is missing or empty, nothing renders. */
function TaskDetail({
  taskKey,
  details,
}: {
  taskKey: string;
  details: GenerationProgressDetails;
}) {
  const { t } = useTranslation();

  if (taskKey === "memory") {
    const facts = details.facts ?? [];
    if (facts.length === 0) return null;
    return (
      <DetailReveal>
        <div className="text-[10.5px] text-muted-foreground/60">
          {t("generationProgress.details.priorCases", { count: facts.length })}
        </div>
        <div className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
          <History className="h-3 w-3 shrink-0 mt-0.5 text-info-muted" />
          <span className="truncate">{facts[0]}</span>
        </div>
      </DetailReveal>
    );
  }

  if (taskKey === "understand") {
    const questions = details.questions ?? [];
    if (questions.length === 0) return null;
    return (
      <DetailReveal>
        <div className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
          <MessageCircleQuestion className="h-3 w-3 shrink-0 mt-0.5 text-primary-accent/60" />
          <span className="line-clamp-2">{questions[0]}</span>
        </div>
        {questions.length > 1 && (
          <div className="pl-[18px] text-[10.5px] text-muted-foreground/60">
            {t("generationProgress.details.moreQuestions", {
              count: questions.length - 1,
            })}
          </div>
        )}
      </DetailReveal>
    );
  }

  if (taskKey === "search") {
    const queries = details.searchQueries ?? [];
    const identifiers = details.extractedIdentifiers ?? [];
    const hitCount = details.searchHitCount;
    if (queries.length === 0 && identifiers.length === 0 && hitCount == null) return null;
    return (
      <DetailReveal>
        {hitCount != null && (
          <div className="text-[10.5px] text-muted-foreground/60">
            {t("generationProgress.details.hits", { count: hitCount })}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-1">
          {queries.slice(0, 2).map((query, i) => (
            <span
              key={i}
              className="max-w-full truncate rounded-[5px] bg-[#07195F]/[0.05] px-1.5 py-0.5 text-[10px] text-muted-foreground"
            >
              {query}
            </span>
          ))}
          {identifiers.length > 0 && (
            <span className="max-w-full truncate rounded-[5px] border border-info-line bg-surface-subtle px-1.5 py-0.5 font-mono text-[10px] text-info-strong">
              {identifiers[0]}
            </span>
          )}
          {queries.length > 2 && (
            <span className="rounded-[5px] px-0.5 py-0.5 text-[10px] text-muted-foreground/60">
              {t("generationProgress.details.more", { count: queries.length - 2 })}
            </span>
          )}
        </div>
      </DetailReveal>
    );
  }

  if (taskKey === "rerank") {
    const refs = details.references ?? [];
    // Top sources as compact rows with a file icon; only those that carry a
    // title render. Anything beyond the first two collapses into a count line.
    const titled = refs.filter((r) => !!r.title);
    if (titled.length === 0) return null;
    const shown = titled.slice(0, 2);
    const remaining = titled.length - shown.length;
    return (
      <DetailReveal>
        {shown.map((ref) => (
          <div
            key={ref.index}
            className="flex items-center gap-1.5 text-[11px] text-muted-foreground"
          >
            <FileText className="h-3 w-3 shrink-0 text-info-muted" />
            <span className="truncate">{ref.title}</span>
          </div>
        ))}
        {remaining > 0 && (
          <div className="pl-[18px] text-[10.5px] text-muted-foreground/60">
            {t("generationProgress.details.moreSources", { count: remaining })}
          </div>
        )}
      </DetailReveal>
    );
  }

  if (taskKey === "generate") {
    // The longest LLM wait: show what the draft is being built from. The
    // latched reference count is the honest basis count; render only when
    // references actually exist.
    const refCount = (details.references ?? []).length;
    if (refCount === 0) return null;
    return (
      <DetailReveal>
        <div className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
          <PenLine className="h-3 w-3 shrink-0 mt-0.5 text-primary-accent/60" />
          <span>{t("generationProgress.details.draftingFromSources", { count: refCount })}</span>
        </div>
      </DetailReveal>
    );
  }

  return null;
}

export function GenerationProgressPanel({
  state,
  seenStages,
  details,
  errorMessage,
  includeSyncTask = false,
  syncPhase = "done",
  mailContext = null,
  onContinue,
  onRetry,
}: GenerationProgressPanelProps) {
  const { t, i18n } = useTranslation();

  // Elapsed seconds since the current run started. Resets whenever the panel
  // (re-)enters the running state, freezes on done/failed.
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  // After "Weiter warten", the action pair stays hidden until the reappear
  // threshold so the user isn't immediately re-prompted.
  const [keepWaitingDismissed, setKeepWaitingDismissed] = useState(false);
  const wasRunningRef = useRef(false);

  useEffect(() => {
    if (state !== "running") {
      wasRunningRef.current = false;
      return;
    }
    if (!wasRunningRef.current) {
      wasRunningRef.current = true;
      setElapsedSeconds(0);
      setKeepWaitingDismissed(false);
    }
    const start = Date.now();
    const id = setInterval(() => {
      setElapsedSeconds((prev) => {
        const next = Math.floor((Date.now() - start) / 1000);
        return next > prev ? next : prev;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [state]);

  if (state === "idle") {
    return (
      <Card className="border-t-0">
        <CardContent className="flex flex-col items-center gap-3 py-8 px-4 text-center">
          <Info className="h-5 w-5 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            {t("generationProgress.idle")}
          </p>
          <Button variant="ghost" size="sm" className="text-xs" onClick={onContinue}>
            {t("generationProgress.toSources")}
            <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </CardContent>
      </Card>
    );
  }

  const maxSeenRank = Math.max(
    -1,
    ...[...seenStages].map((s) => STAGE_RANK[s] ?? -1),
  );

  // Full pipeline of this run: every unconditional task plus the conditional
  // ones whose trigger stage has been observed. This is the denominator of
  // the progress bar, independent of which rows are currently revealed.
  const pipelineTasks = TASKS.filter(
    (task) =>
      !task.appearsOnStages ||
      task.appearsOnStages.some((s) => seenStages.has(s)),
  );

  const doneByKey = new Map(
    pipelineTasks.map((task) => [
      task.key,
      state === "done" || isTaskDone(task, maxSeenRank),
    ]),
  );
  const doneCount = [...doneByKey.values()].filter(Boolean).length;
  // While the intake phase is still active, it owns the spinner and no
  // pipeline task has started yet.
  const syncActive = includeSyncTask && syncPhase !== "done";
  const activeKey =
    !syncActive && (state === "running" || state === "failed")
      ? (pipelineTasks.find((task) => !doneByKey.get(task.key))?.key ?? null)
      : null;

  // Progressive reveal: a row appears only once the backend has reached it —
  // finished steps plus the one currently running. Upcoming steps stay hidden,
  // so the checklist mirrors what actually happens in this run instead of
  // promising a fixed sequence.
  const visibleTasks = pipelineTasks.filter(
    (task) => doneByKey.get(task.key) === true || task.key === activeKey,
  );

  // End-of-run dead-air guard. Between `generated` and `completed` the backend
  // is still finishing (scoring / refinement window) but every revealed row is
  // already done, so there is no spinner and the panel looks frozen. While the
  // run is still going and nothing else is active, keep the last revealed row
  // in the active visual — it is honest, the pipeline genuinely is still
  // finishing that phase of work. The done→active flip never fights the
  // terminal transition: once `state` becomes "done", `activeKey` is null AND
  // this guard is gated on `state === "running"`, so every row renders done.
  const tailActiveKey =
    state === "running" && !syncActive && activeKey === null && visibleTasks.length > 0
      ? visibleTasks[visibleTasks.length - 1].key
      : null;

  // Count the intake rows in the progress fraction so the bar doesn't jump
  // when the pipeline phase takes over from the intake phase.
  const syncDoneCount =
    syncPhase === "done" ? 2 : syncPhase === "annotating" ? 1 : 0;
  const totalTaskCount = pipelineTasks.length + (includeSyncTask ? 2 : 0);
  const totalDoneCount = doneCount + (includeSyncTask ? syncDoneCount : 0);
  const progressFraction =
    state === "done" ? 1 : Math.max(0.06, totalDoneCount / totalTaskCount);

  const showSyncHeader = syncActive && state === "running";
  const isOverrun = state === "running" && elapsedSeconds >= OVERRUN_SUBTITLE_SECONDS;
  const showOverrunActions =
    state === "running" &&
    elapsedSeconds >= OVERRUN_ACTIONS_SECONDS &&
    (!keepWaitingDismissed || elapsedSeconds >= OVERRUN_ACTIONS_REAPPEAR_SECONDS);

  const showMailCard = !!mailContext && !!(mailContext.subject ?? mailContext.senderName);
  const receivedAtValid =
    !!mailContext?.receivedAt && !Number.isNaN(mailContext.receivedAt.getTime());

  const subtitle =
    state === "failed"
      ? t("generationProgress.failedSubtitle")
      : state === "done"
        ? elapsedSeconds > 0
          ? `${t("generationProgress.doneSubtitle")} · ${t("generationProgress.doneElapsed", { seconds: elapsedSeconds })}`
          : t("generationProgress.doneSubtitle")
        : isOverrun
          ? t("generationProgress.overrunSubtitle")
          : showSyncHeader
            ? t("generationProgress.syncSubtitle")
            : t("generationProgress.runningSubtitle");

  // Tasks grouped under their act sections, preserving pipeline order.
  const analysisTasks = visibleTasks.filter((task) => task.act === "analysis");
  const answerTasks = visibleTasks.filter((task) => task.act === "answer");

  // Synthetic intake rows ("E-Mail empfangen", "Anliegen wird analysiert") —
  // outside the broadcast pipeline (no STAGE_RANK), driven purely by
  // syncPhase: the mail fetch and the request analysis are separate backend
  // steps, so each gets its own row instead of one long opaque spinner. Like
  // the pipeline rows, an intake row only exists once its step has started,
  // so it is always either done or active.
  // Shared row shell: a [rail | body] grid. The rail column holds the node and,
  // when the row is not the last in its act segment, a connector that fills as
  // the row completes. The active row is marked by a background tint + a
  // semibold label (the spinning node is the only ambient looping signal).
  const renderRow = ({
    key,
    rowState,
    label,
    hasConnector,
    connectorFilled,
    detail,
  }: {
    key: string;
    rowState: RailNodeState;
    label: string;
    hasConnector: boolean;
    connectorFilled: boolean;
    detail?: React.ReactNode;
  }) => {
    const isActive = rowState === "active";
    const isFailed = rowState === "failed";
    return (
      <motion.li
        key={key}
        layout
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        data-testid={`generation-task-${key}`}
        data-task-state={rowState}
        className="grid grid-cols-[18px_1fr] items-stretch gap-x-2.5"
      >
        {/* Rail column: node + connector to the next row in the segment. The
            connector stretches to fill the row height and bridges into the
            next node (rows sit flush, so the rail reads as continuous). */}
        <div className="flex flex-col items-center pt-1.5 pb-0">
          <RailNode state={rowState} />
          {hasConnector && <RailConnector filled={connectorFilled} />}
        </div>
        {/* Body column. */}
        <div
          className={`min-w-0 rounded-md px-2 py-1 ${
            isActive ? "bg-muted/50" : ""
          }`}
        >
          <span
            className={`text-sm ${
              isActive || isFailed
                ? "font-medium text-foreground"
                : "text-foreground"
            }`}
          >
            {label}
          </span>
          {detail ? (
            <AnimatePresence initial={false}>{detail}</AnimatePresence>
          ) : null}
        </div>
      </motion.li>
    );
  };

  const renderIntakeRow = (
    key: "sync" | "annotate",
    rowState: "done" | "active",
    hasConnector: boolean,
  ) =>
    renderRow({
      key,
      rowState,
      hasConnector,
      // The intake segment never holds a failed node, so a done row's
      // connector always fills.
      connectorFilled: rowState === "done",
      label: t(
        rowState === "done"
          ? `generationProgress.tasksDone.${key}`
          : `generationProgress.tasks.${key}`,
      ),
    });

  // A connector fills once its row is done, EXCEPT the connector leading into
  // the failed row, which stays neutral (failure interrupts the rail).
  const renderTaskRow = (
    task: TaskDef,
    hasConnector: boolean,
    nextIsFailed: boolean,
  ) => {
    const done = doneByKey.get(task.key) ?? false;
    const isActive = state !== "done" && task.key === activeKey;
    const isFailedHere = state === "failed" && isActive;
    // Dead-air tail: a row that has finished its own step but is held in the
    // active visual because the run is still wrapping up. It keeps its done
    // label (the step really is complete) while showing the spinning node and
    // tint, so there is never a frozen, motionless moment before completion.
    const isTailActive = task.key === tailActiveKey;
    const rowState: RailNodeState = isFailedHere
      ? "failed"
      : isTailActive
        ? "active"
        : done
          ? "done"
          : "active";
    return renderRow({
      key: task.key,
      rowState,
      hasConnector,
      // A held tail row is logically done, so its connector still fills.
      connectorFilled: (done || rowState === "done") && !nextIsFailed,
      label: t(
        done
          ? `generationProgress.tasksDone.${task.key}`
          : `generationProgress.tasks.${task.key}`,
      ),
      detail:
        details && (done || isActive || isTailActive) && !isFailedHere ? (
          <TaskDetail taskKey={task.key} details={details} />
        ) : undefined,
    });
  };

  return (
    <Card className="border-t-0 overflow-hidden">
      <MotionConfig reducedMotion="user">
        <CardContent className="px-4 pt-4 pb-5 space-y-4">
          {/* E-Mail context card — rendered from real mail data, instantly */}
          {showMailCard && mailContext && (
            <div className="flex items-start gap-2.5 rounded-lg border border-border bg-card px-3 py-2.5">
              {mailContext.senderName && (
                <div
                  className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-lg text-xs font-semibold"
                  style={{ background: "#07195F", color: "#80E4FB" }}
                >
                  {senderInitials(mailContext.senderName)}
                </div>
              )}
              <div className="min-w-0 flex-1">
                {(mailContext.senderName ?? receivedAtValid) && (
                  <div className="flex items-baseline gap-2">
                    {mailContext.senderName && (
                      <span className="truncate text-xs font-semibold text-foreground">
                        {mailContext.senderName}
                      </span>
                    )}
                    {receivedAtValid && (
                      <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
                        {mailContext.receivedAt!.toLocaleTimeString(i18n.language, {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    )}
                  </div>
                )}
                {mailContext.subject && (
                  <p className="truncate text-xs text-muted-foreground">
                    {mailContext.subject}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Header */}
          <div className="flex items-start gap-3">
            <div
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white"
              style={{
                background:
                  "linear-gradient(to bottom right, #D571E5, #80E4FB)",
              }}
            >
              {state === "running" ? (
                <Sparkles className="h-4.5 w-4.5" />
              ) : state === "failed" ? (
                <AlertTriangle className="h-4.5 w-4.5" />
              ) : (
                <Check className="h-4.5 w-4.5" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-foreground">
                {state === "failed"
                  ? t("generationProgress.failedTitle")
                  : state === "done"
                    ? t("generationProgress.doneTitle")
                    : showSyncHeader
                      ? t("generationProgress.syncTitle")
                      : t("generationProgress.runningTitle")}
              </p>
              <p className="text-xs tabular-nums text-muted-foreground">{subtitle}</p>
            </div>
            {state === "running" && (
              <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium tabular-nums text-muted-foreground">
                {formatElapsed(elapsedSeconds)}
              </span>
            )}
          </div>

          {/* Slim overall-progress bar. The rail only reflects revealed steps
              (future ones are hidden), so this keeps a subtle at-a-glance
              fraction of the whole pipeline, including the intake rows —
              complementary to, not redundant with, the rail. */}
          <div className="h-[2px] w-full rounded-full bg-muted overflow-hidden">
            <motion.div
              className="h-full rounded-full"
              style={{
                background: "linear-gradient(to right, #345AEF, #80E4FB)",
              }}
              initial={{ width: 0 }}
              animate={{ width: `${progressFraction * 100}%` }}
              transition={{ type: "spring", stiffness: 90, damping: 20 }}
            />
          </div>

          {/* Task checklist with live detail, grouped into act sections. Rows
              and section labels appear progressively as the run reaches them;
              a label only renders once its section has a visible row. */}
          <ul>
            <AnimatePresence initial={false}>
              {includeSyncTask && (
                <ActLabel key="act-intake" label={t("generationProgress.acts.intake")} />
              )}
              {/* Intake segment: the "sync" row connects to "annotate" only
                  while the annotate row is actually visible. */}
              {includeSyncTask &&
                renderIntakeRow(
                  "sync",
                  syncPhase === "fetching" ? "active" : "done",
                  syncPhase !== "fetching",
                )}
              {includeSyncTask &&
                syncPhase !== "fetching" &&
                renderIntakeRow(
                  "annotate",
                  syncPhase === "done" ? "done" : "active",
                  false,
                )}
              {analysisTasks.length > 0 && (
                <ActLabel key="act-analysis" label={t("generationProgress.acts.analysis")} />
              )}
              {analysisTasks.map((task, i) => {
                const next = analysisTasks[i + 1];
                return renderTaskRow(
                  task,
                  i < analysisTasks.length - 1,
                  state === "failed" && next?.key === activeKey,
                );
              })}
              {answerTasks.length > 0 && (
                <ActLabel key="act-answer" label={t("generationProgress.acts.answer")} />
              )}
              {answerTasks.map((task, i) => {
                const next = answerTasks[i + 1];
                return renderTaskRow(
                  task,
                  i < answerTasks.length - 1,
                  state === "failed" && next?.key === activeKey,
                );
              })}
            </AnimatePresence>
          </ul>

          {/* Failure card — bordered card with retry + sources actions. The
              technical detail renders only when the backend supplied one. */}
          {state === "failed" && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-2 rounded-lg border border-border border-l-[3px] border-l-destructive bg-card px-3.5 py-3"
            >
              <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
                {t("generationProgress.failure.title")}
              </div>
              <p className="text-xs leading-relaxed text-muted-foreground">
                {t("generationProgress.failure.body")}
              </p>
              {errorMessage && (
                <details>
                  <summary className="cursor-pointer text-[11px] text-muted-foreground/70 hover:text-foreground">
                    {t("generationProgress.failure.technicalDetails")}
                  </summary>
                  <code className="mt-1.5 block break-words rounded bg-muted px-2 py-1.5 text-[11px] text-muted-foreground">
                    {errorMessage}
                  </code>
                </details>
              )}
              <div className="flex gap-2 pt-1">
                <Button size="sm" className="flex-1 text-xs" onClick={onRetry}>
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                  {t("generationProgress.retry")}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1 text-xs"
                  onClick={onContinue}
                >
                  {t("generationProgress.toSources")}
                  <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Footer while running: honest expectation and the overrun action
              pair once the wait gets long. */}
          {state === "running" && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
                <Clock className="h-3 w-3 shrink-0" />
                {/* Intake (import + analysis) takes longer than the pipeline
                    itself, so the expectation line is phase-specific. */}
                <span>
                  {t(
                    syncActive
                      ? "generationProgress.syncExpectation"
                      : "generationProgress.expectation",
                  )}
                </span>
              </div>
              {showOverrunActions && (
                <div className="flex gap-2 pt-1">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 text-xs"
                    onClick={() => setKeepWaitingDismissed(true)}
                  >
                    {t("generationProgress.keepWaiting")}
                  </Button>
                  <Button size="sm" className="flex-1 text-xs" onClick={onRetry}>
                    <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                    {t("generationProgress.restart")}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Done banner — parent auto-advances shortly after; the button is
              there for users who want to jump immediately. */}
          {state === "done" && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Button size="sm" className="w-full text-xs" onClick={onContinue}>
                {t("generationProgress.continue")}
                <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
              </Button>
            </motion.div>
          )}
        </CardContent>
      </MotionConfig>
    </Card>
  );
}
